import { MenuItem, Select, Typography } from '@material-ui/core';
import React, { ReactElement, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Control, FieldError } from 'react-hook-form/dist/types';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';
import { useSelector } from 'react-redux';

import { LabelWithUIGuidelines } from 'component-library/atoms/LabelWithUIGuidelines';
import { appConfigSelector } from 'reducers/app.reducer';
import { buildCountryOptions } from '../../constants';
import {
  FieldErrorText,
  InputContainer,
  PlaceholderText,
  StyledSelect,
  TitleContainer,
} from './RHF.styles';

type RHFCountrySelectProps = {
  name: string;
  control: Control;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  addons?: ReactElement;
  error?: FieldError;
  rules?: RegisterOptions;
};

/**
 * Champ select de pays compatible React Hook Form v6.
 * Croise la liste COUNTRIES avec deliveryCountriesIds de la config app
 * (même logique que le composant DeliveryCountry du BO).
 */
const RHFCountrySelect = ({
  name,
  control,
  label,
  placeholder,
  required = false,
  disabled = false,
  addons,
  error,
  rules = {},
}: RHFCountrySelectProps): JSX.Element => {
  const appConfig = useSelector(appConfigSelector);

  const countryOptions = useMemo(
    () => buildCountryOptions(appConfig.deliveryCountriesIds),
    [appConfig.deliveryCountriesIds],
  );

  return (
    <InputContainer>
      <TitleContainer>
        <LabelWithUIGuidelines label={label} required={required} />
        {addons}
      </TitleContainer>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ onChange, value }) => (
          <StyledSelect>
            <Select
              name={name}
              fullWidth
              variant="outlined"
              displayEmpty
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              error={!!error}
            >
              {placeholder && (
                <MenuItem value="" disabled>
                  <PlaceholderText>{placeholder}</PlaceholderText>
                </MenuItem>
              )}
              {countryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </StyledSelect>
        )}
      />
      {error && (
        <FieldErrorText>
          <Typography variant="caption" color="error">
            {error.message}
          </Typography>
        </FieldErrorText>
      )}
    </InputContainer>
  );
};

export default RHFCountrySelect;
